import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2Complete } from '@/lib/q7_2Storage';
import { isQ7_3Complete } from '@/lib/q7_3Storage';
import { isQ7_4CompleteForSurvey } from '@/lib/q7_4Storage';
import { isDevRegComplete } from '@/lib/devregStorage';
import { isSwitchComplete } from '@/lib/switchStorage';
import { isSwitchReasonComplete } from '@/lib/switchReasonStorage';
import {
  NPD_CORRECT_ANSWER,
  type NpdAwarenessAnswer,
  readNpdAwarenessAnswers,
  writeNpdAwarenessAnswers,
} from '@/lib/npdAwarenessStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

type NpdAwarenessOption = {
  id: NpdAwarenessAnswer;
  labelKey: 'optionMuscatGrape' | 'optionBerry' | 'optionLychee';
};
const OPTION_KEYS: NpdAwarenessOption[] = [
  { id: 'muscat_grape', labelKey: 'optionMuscatGrape' },
  { id: 'berry', labelKey: 'optionBerry' },
  { id: 'lychee', labelKey: 'optionLychee' },
];

const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-[15px] font-medium leading-snug text-white/95 sm:text-[16px]';

const checkboxClass =
  'mt-0.5 h-[18px] w-[18px] shrink-0 rounded border border-slate-300/90 bg-white text-amber-800 focus:ring-1 focus:ring-slate-300';

function RatingDots({ filled, total = 5 }: { filled: number; total?: number }) {
  return (
    <span className="inline-flex items-center gap-1.5" aria-hidden>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={
            i < filled
              ? 'h-3 w-3 rounded-full bg-white'
              : 'h-3 w-3 rounded-full border border-white/55 bg-transparent'
          }
        />
      ))}
    </span>
  );
}

export default function NpdAwareness() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [choice, setChoice] = useState<NpdAwarenessAnswer | null>(
    () => readNpdAwarenessAnswers()?.flavour ?? null
  );
  const navigateAfterCheckRef = useRef(false);

  useEffect(() => {
    if (!navigateAfterCheckRef.current) return;
    navigateAfterCheckRef.current = false;
    navigate(choice === NPD_CORRECT_ANSWER ? '/npd-correct' : '/npd-wrong');
  }, [choice, navigate]);

  const pick = (v: NpdAwarenessAnswer) => {
    setChoice((prev) => {
      if (prev === v) return prev;
      writeNpdAwarenessAnswers({ flavour: v });
      navigateAfterCheckRef.current = true;
      return v;
    });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2Complete()) {
    return <Navigate to="/7_2" replace />;
  }
  if (!isQ7_3Complete()) {
    return <Navigate to="/7_3" replace />;
  }
  if (!isQ7_4CompleteForSurvey()) {
    return <Navigate to="/7_4" replace />;
  }
  if (!isDevRegComplete()) {
    return <Navigate to="/devreg" replace />;
  }
  if (!isSwitchComplete()) {
    return <Navigate to="/switch" replace />;
  }
  if (!isSwitchReasonComplete()) {
    return <Navigate to="/switch-reason" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!p-4">
              <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                {t.npdAwareness.question}
              </p>
              <div className="mt-5 grid w-max grid-cols-[auto_auto] items-center gap-x-3 gap-y-2.5">
                <span className="text-left text-[15px] font-medium text-white/95 sm:text-[16px]">
                  {t.npdAwareness.intensityLabel}
                </span>
                <RatingDots filled={3} />
                <span className="text-left text-[15px] font-medium text-white/95 sm:text-[16px]">
                  {t.npdAwareness.coolingLabel}
                </span>
                <RatingDots filled={3} />
              </div>
              <div className="mt-5 flex flex-col gap-3">
                {OPTION_KEYS.map((opt) => {
                  const id = `npd-awareness-${opt.id}`;
                  const checked = choice === opt.id;
                  return (
                    <label key={opt.id} htmlFor={id} className={checkboxLabelClass}>
                      <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={() => pick(opt.id)}
                        className={checkboxClass}
                      />
                      <span>{t.npdAwareness[opt.labelKey]}</span>
                    </label>
                  );
                })}
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
